import React, { useEffect, useState } from "react";
import {
  Plus,
  Pencil,
  Trash2,
  X,
  FolderTree,
  ChevronRight,
  ChevronUp,
  ChevronDown,
} from "lucide-react";
import { toast } from "sonner";
import api, { imgUrl, formatApiErrorDetail } from "../../lib/api";
import { ImageUpload } from "../../components/admin/ImageUpload";

const empty = {
  name: "",
  description: "",
  image: "",
  parent_id: "",
  show_on_home: true,
  sort_order: 0,
};

export default function AdminCategories() {
  const [cats, setCats] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ ...empty });
  const [saving, setSaving] = useState(false);
  const [reordering, setReordering] = useState(false);

  // =========================================================
  // LOAD CATEGORIES
  // =========================================================

  const load = () =>
    api
      .get("/categories")
      .then(({ data }) => setCats(Array.isArray(data) ? data : []))
      .catch(() => {});

  useEffect(() => {
    load();
  }, []);

  // =========================================================
  // MAIN CATEGORIES
  // =========================================================

  const mainCategories = cats
    .filter((c) => !c.parent_id || c.parent_id === "")
    .sort((a, b) => {
      const orderA = Number(a.sort_order ?? 0);
      const orderB = Number(b.sort_order ?? 0);

      if (orderA !== orderB) {
        return orderA - orderB;
      }

      return (a.name || "").localeCompare(b.name || "", "el");
    });

  // =========================================================
  // SUBCATEGORIES
  // =========================================================

  const getSubcategories = (parentId) =>
    cats
      .filter((c) => c.parent_id === parentId)
      .sort((a, b) => {
        const orderA = Number(a.sort_order ?? 0);
        const orderB = Number(b.sort_order ?? 0);

        if (orderA !== orderB) {
          return orderA - orderB;
        }

        return (a.name || "").localeCompare(b.name || "", "el");
      });

  // =========================================================
  // NEW CATEGORY
  // =========================================================

  const openNew = () => {
    setEditing(null);
    setForm({ ...empty });
    setOpen(true);
  };

  // =========================================================
  // EDIT CATEGORY
  // =========================================================

  const openEdit = (c) => {
    setEditing(c);

    setForm({
      name: c.name || "",
      description: c.description || "",
      image: c.image || "",
      parent_id: c.parent_id || "",
      show_on_home: c.show_on_home !== false,
      sort_order: Number(c.sort_order ?? 0),
    });

    setOpen(true);
  };

  // =========================================================
  // FORM SETTER
  // =========================================================

  const set = (key) => (e) => {
    setForm((current) => ({
      ...current,
      [key]: e.target.value,
    }));
  };

  // =========================================================
  // CATEGORY PAYLOAD
  // =========================================================

  const categoryPayload = (category, sortOrder) => ({
    name: category.name || "",
    description: category.description || "",
    image: category.image || "",
    parent_id: category.parent_id || "",
    show_on_home: category.show_on_home !== false,
    sort_order: Number(sortOrder ?? 0),
  });

  // =========================================================
  // MOVE MAIN CATEGORY
  // =========================================================

  const moveMainCategory = async (index, direction) => {
    if (reordering) return;

    const targetIndex = index + direction;

    if (
      targetIndex < 0 ||
      targetIndex >= mainCategories.length
    ) {
      return;
    }

    setReordering(true);

    try {
      /*
       * Δημιουργούμε τη νέα σειρά.
       */
      const reordered = [...mainCategories];

      const current = reordered[index];
      const target = reordered[targetIndex];

      reordered[index] = target;
      reordered[targetIndex] = current;

      /*
       * Πολύ σημαντικό:
       * Αποθηκεύουμε ΟΛΕΣ τις κύριες κατηγορίες με
       * συνεχόμενους αριθμούς 0, 1, 2, 3...
       *
       * Έτσι διορθώνεται και η σειρά παλιών κατηγοριών
       * που μπορεί να μην είχαν ακόμη sort_order.
       */
      await Promise.all(
        reordered.map((category, newIndex) =>
          api.put(
            `/categories/${category.id}`,
            categoryPayload(category, newIndex)
          )
        )
      );

      /*
       * Ξαναφορτώνουμε τις κατηγορίες από τη βάση.
       */
      await load();

      toast.success("Η σειρά των κατηγοριών ενημερώθηκε");
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) ||
          "Δεν ήταν δυνατή η αλλαγή σειράς"
      );
    } finally {
      setReordering(false);
    }
  };

  // =========================================================
  // SAVE
  // =========================================================

  const save = async (e) => {
    e.preventDefault();

    if (!form.name.trim()) {
      toast.error("Συμπληρώστε όνομα");
      return;
    }

    if (editing && form.parent_id === editing.id) {
      toast.error(
        "Μια κατηγορία δεν μπορεί να είναι γονική του εαυτού της"
      );
      return;
    }

    setSaving(true);

    try {
      const payload = {
        name: form.name.trim(),
        description: form.description || "",
        image: form.image || "",
        parent_id: form.parent_id || "",
        show_on_home: form.show_on_home !== false,
        sort_order: Number(form.sort_order ?? 0),
      };

      if (editing) {
        await api.put(`/categories/${editing.id}`, payload);
      } else {
        await api.post("/categories", payload);
      }

      toast.success(
        editing
          ? "Η κατηγορία ενημερώθηκε"
          : "Η κατηγορία προστέθηκε"
      );

      setOpen(false);
      setEditing(null);
      setForm({ ...empty });

      await load();
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) ||
          "Σφάλμα"
      );
    } finally {
      setSaving(false);
    }
  };

  // =========================================================
  // DELETE
  // =========================================================

  const del = async (c) => {
    const subcategories = getSubcategories(c.id);

    if (subcategories.length > 0) {
      const confirmed = window.confirm(
        `Η κατηγορία «${c.name}» έχει ${subcategories.length} υποκατηγορ${
          subcategories.length === 1 ? "ία" : "ίες"
        }.\n\nΘέλετε σίγουρα να τη διαγράψετε;`
      );

      if (!confirmed) return;
    } else {
      if (
        !window.confirm(
          `Διαγραφή της κατηγορίας «${c.name}»;`
        )
      ) {
        return;
      }
    }

    try {
      await api.delete(`/categories/${c.id}`);

      toast.success("Η κατηγορία διαγράφηκε");

      await load();
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) ||
          "Σφάλμα"
      );
    }
  };

  // =========================================================
  // INPUT STYLE
  // =========================================================

  const inputCls =
    "w-full px-3 py-2.5 rounded-sm border border-[#E5E9E4] bg-white text-sm focus:border-[#1E3A2F] focus:ring-1 focus:ring-[#1E3A2F] outline-none";

  // =========================================================
  // RENDER
  // =========================================================

  return (
    <div>
      {/* =====================================================
          HEADER
      ====================================================== */}

      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-serif-display text-3xl text-[#1E3A2F]">
            Κατηγορίες
          </h1>

          <p className="text-sm text-[#536D61] mt-1">
            Σύρε τη σειρά των κύριων κατηγοριών με τα ↑ ↓
            κουμπιά.
          </p>
        </div>

        <button
          onClick={openNew}
          data-testid="add-category-button"
          className="inline-flex items-center gap-2 bg-[#1E3A2F] text-white px-4 py-2.5 rounded-sm text-sm font-medium hover:bg-[#111C18]"
        >
          <Plus className="h-4 w-4" />
          Νέα κατηγορία
        </button>
      </div>

      {/* =====================================================
          CATEGORIES
      ====================================================== */}

      {cats.length > 0 ? (
        <div className="space-y-6">
          {mainCategories.map((c, index) => {
            const subcategories = getSubcategories(c.id);

            return (
              <div key={c.id}>
                {/* =================================================
                    MAIN CATEGORY
                ================================================== */}

                <div
                  className="bg-white border border-[#E5E9E4] rounded-sm overflow-hidden"
                  data-testid={`category-row-${c.id}`}
                >
                  <div className="h-32 bg-[#E8F0E1] flex items-center justify-center">
                    {c.image ? (
                      <img
                        src={imgUrl(c.image)}
                        alt=""
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <FolderTree className="h-8 w-8 text-[#1E3A2F]/40" />
                    )}
                  </div>

                  <div className="p-4">
                    <div className="flex items-start gap-3">
                      {/* ORDER NUMBER */}

                      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-[#E8F0E1] text-[#1E3A2F] flex items-center justify-center text-sm font-semibold">
                        {index + 1}
                      </div>

                      {/* CATEGORY INFO */}

                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <FolderTree className="h-4 w-4 text-[#1E3A2F]" />

                          <p className="font-medium text-[#1E3A2F]">
                            {c.name}
                          </p>

                          <span className="text-xs bg-[#E8F0E1] text-[#536D61] px-2 py-1 rounded-full">
                            Κύρια κατηγορία
                          </span>

                          {c.show_on_home !== false && (
                            <span className="text-xs bg-[#F1F5EE] text-[#536D61] px-2 py-1 rounded-full">
                              Αρχική
                            </span>
                          )}
                        </div>

                        {c.description && (
                          <p className="text-sm text-[#536D61] line-clamp-2 mt-1">
                            {c.description}
                          </p>
                        )}
                      </div>

                      {/* ORDER BUTTONS */}

                      <div className="flex flex-col gap-1 flex-shrink-0">
                        <button
                          type="button"
                          onClick={() =>
                            moveMainCategory(index, -1)
                          }
                          disabled={
                            index === 0 || reordering
                          }
                          title="Μετακίνηση προς τα πάνω"
                          aria-label={`Μετακίνηση ${c.name} προς τα πάνω`}
                          className="w-9 h-9 flex items-center justify-center rounded-sm border border-[#E5E9E4] text-[#1E3A2F] hover:bg-[#F1F5EE] disabled:opacity-30 disabled:cursor-not-allowed"
                        >
                          <ChevronUp className="h-5 w-5" />
                        </button>

                        <button
                          type="button"
                          onClick={() =>
                            moveMainCategory(index, 1)
                          }
                          disabled={
                            index ===
                              mainCategories.length - 1 ||
                            reordering
                          }
                          title="Μετακίνηση προς τα κάτω"
                          aria-label={`Μετακίνηση ${c.name} προς τα κάτω`}
                          className="w-9 h-9 flex items-center justify-center rounded-sm border border-[#E5E9E4] text-[#1E3A2F] hover:bg-[#F1F5EE] disabled:opacity-30 disabled:cursor-not-allowed"
                        >
                          <ChevronDown className="h-5 w-5" />
                        </button>
                      </div>
                    </div>

                    {/* ACTIONS */}

                    <div className="flex gap-2 mt-4 pt-3 border-t border-[#E5E9E4]">
                      <button
                        onClick={() => openEdit(c)}
                        data-testid={`edit-category-${c.id}`}
                        className="flex items-center gap-1 text-sm text-[#1E3A2F] hover:underline"
                      >
                        <Pencil className="h-3.5 w-3.5" />
                        Επεξεργασία
                      </button>

                      <button
                        onClick={() => del(c)}
                        data-testid={`delete-category-${c.id}`}
                        className="flex items-center gap-1 text-sm text-[#C25953] hover:underline ml-auto"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                        Διαγραφή
                      </button>
                    </div>
                  </div>
                </div>

                {/* =================================================
                    SUBCATEGORIES
                ================================================== */}

                {subcategories.length > 0 && (
                  <div className="mt-3 ml-6 pl-5 border-l-2 border-[#E5E9E4] grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {subcategories.map((sub) => (
                      <div
                        key={sub.id}
                        className="bg-white border border-[#E5E9E4] rounded-sm overflow-hidden"
                        data-testid={`category-row-${sub.id}`}
                      >
                        <div className="h-24 bg-[#F1F5EE] flex items-center justify-center">
                          {sub.image ? (
                            <img
                              src={imgUrl(sub.image)}
                              alt=""
                              className="h-full w-full object-cover"
                            />
                          ) : (
                            <FolderTree className="h-7 w-7 text-[#1E3A2F]/40" />
                          )}
                        </div>

                        <div className="p-4">
                          <div className="flex items-center gap-1.5">
                            <ChevronRight className="h-4 w-4 text-[#536D61]" />

                            <p className="font-medium text-[#1E3A2F]">
                              {sub.name}
                            </p>

                            <span className="text-xs bg-[#F1F5EE] text-[#536D61] px-2 py-1 rounded-full ml-1">
                              Υποκατηγορία
                            </span>
                          </div>

                          {sub.description && (
                            <p className="text-sm text-[#536D61] line-clamp-2 mt-1">
                              {sub.description}
                            </p>
                          )}

                          <div className="flex gap-2 mt-3">
                            <button
                              onClick={() => openEdit(sub)}
                              data-testid={`edit-category-${sub.id}`}
                              className="flex items-center gap-1 text-sm text-[#1E3A2F] hover:underline"
                            >
                              <Pencil className="h-3.5 w-3.5" />
                              Επεξεργασία
                            </button>

                            <button
                              onClick={() => del(sub)}
                              data-testid={`delete-category-${sub.id}`}
                              className="flex items-center gap-1 text-sm text-[#C25953] hover:underline ml-auto"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                              Διαγραφή
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}

          {/* =====================================================
              ORPHAN CATEGORIES
          ====================================================== */}

          {cats
            .filter(
              (c) =>
                c.parent_id &&
                !cats.some(
                  (parent) => parent.id === c.parent_id
                )
            )
            .map((c) => (
              <div
                key={`orphan-${c.id}`}
                className="bg-white border border-[#E5E9E4] rounded-sm p-4"
              >
                <div className="flex items-center gap-2">
                  <FolderTree className="h-4 w-4 text-[#C25953]" />

                  <span className="font-medium text-[#1E3A2F]">
                    {c.name}
                  </span>

                  <span className="text-xs text-[#C25953]">
                    Μη έγκυρη γονική κατηγορία
                  </span>
                </div>

                <div className="flex gap-2 mt-3">
                  <button
                    onClick={() => openEdit(c)}
                    className="flex items-center gap-1 text-sm text-[#1E3A2F] hover:underline"
                  >
                    <Pencil className="h-3.5 w-3.5" />
                    Επεξεργασία
                  </button>

                  <button
                    onClick={() => del(c)}
                    className="flex items-center gap-1 text-sm text-[#C25953] hover:underline"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                    Διαγραφή
                  </button>
                </div>
              </div>
            ))}
        </div>
      ) : (
        <div className="bg-white border border-dashed border-[#E5E9E4] rounded-sm py-16 text-center text-[#536D61]">
          Δεν υπάρχουν κατηγορίες ακόμη.
        </div>
      )}

      {/* =====================================================
          CATEGORY MODAL
      ====================================================== */}

      {open && (
        <div className="fixed inset-0 z-50 flex items-start justify-center p-4 overflow-auto bg-black/50">
          <div className="bg-white rounded-sm w-full max-w-md my-8">
            {/* MODAL HEADER */}

            <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E9E4]">
              <h2 className="font-serif-display text-2xl text-[#1E3A2F]">
                {editing
                  ? "Επεξεργασία κατηγορίας"
                  : "Νέα κατηγορία"}
              </h2>

              <button
                type="button"
                onClick={() => setOpen(false)}
                aria-label="Κλείσιμο"
              >
                <X className="h-5 w-5 text-[#536D61]" />
              </button>
            </div>

            {/* FORM */}

            <form onSubmit={save} className="p-6 space-y-4">
              {/* NAME */}

              <div>
                <label className="block text-sm font-medium text-[#1E3A2F] mb-1.5">
                  Όνομα *
                </label>

                <input
                  data-testid="category-name"
                  value={form.name}
                  onChange={set("name")}
                  className={inputCls}
                  required
                />
              </div>

              {/* PARENT */}

              <div>
                <label className="block text-sm font-medium text-[#1E3A2F] mb-1.5">
                  Γονική κατηγορία
                </label>

                <select
                  data-testid="category-parent"
                  value={form.parent_id}
                  onChange={set("parent_id")}
                  className={inputCls}
                >
                  <option value="">
                    — Κύρια κατηγορία —
                  </option>

                  {mainCategories
                    .filter(
                      (c) => !editing || c.id !== editing.id
                    )
                    .map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                </select>

                <p className="text-xs text-[#536D61] mt-1.5">
                  Άφησέ το ως «Κύρια κατηγορία» για νέα
                  κύρια κατηγορία. Επίλεξε μια κύρια
                  κατηγορία για να δημιουργήσεις
                  υποκατηγορία.
                </p>
              </div>

              {/* DESCRIPTION */}

              <div>
                <label className="block text-sm font-medium text-[#1E3A2F] mb-1.5">
                  Περιγραφή
                </label>

                <textarea
                  value={form.description}
                  onChange={set("description")}
                  rows={3}
                  className={inputCls}
                />
              </div>

              {/* IMAGE */}

              <ImageUpload
                value={form.image}
                onChange={(v) =>
                  setForm((current) => ({
                    ...current,
                    image: v,
                  }))
                }
                label="Εικόνα κατηγορίας"
                testid="category-image-upload"
              />

              {/* SHOW ON HOME */}

              <div className="rounded-sm border border-[#E5E9E4] bg-[#F8FAF7] p-3">
                <div className="flex items-center gap-2">
                  <input
                    id="category-show-home"
                    type="checkbox"
                    checked={form.show_on_home}
                    onChange={(e) =>
                      setForm((current) => ({
                        ...current,
                        show_on_home: e.target.checked,
                      }))
                    }
                    className="h-4 w-4"
                  />

                  <label
                    htmlFor="category-show-home"
                    className="text-sm text-[#1E3A2F]"
                  >
                    Εμφάνιση στην αρχική σελίδα
                  </label>
                </div>

                <p className="text-xs text-[#536D61] mt-1.5 ml-6">
                  Η επιλογή αυτή αφορά τις κύριες
                  κατηγορίες. Οι υποκατηγορίες δεν εμφανίζονται
                  ως ξεχωριστές κάρτες στην αρχική.
                </p>
              </div>

              {/* ORDER INFO */}

              {editing &&
                (!editing.parent_id ||
                  editing.parent_id === "") && (
                  <div className="rounded-sm bg-[#F1F5EE] p-3">
                    <p className="text-xs text-[#536D61]">
                      <strong className="text-[#1E3A2F]">
                        Σειρά στην αρχική:
                      </strong>{" "}
                      {mainCategories.findIndex(
                        (c) => c.id === editing.id
                      ) + 1}
                    </p>

                    <p className="text-xs text-[#536D61] mt-1">
                      Χρησιμοποίησε τα ↑ ↓ στην οθόνη
                      «Κατηγορίες» για να αλλάξεις τη σειρά.
                    </p>
                  </div>
                )}

              {/* BUTTONS */}

              <div className="flex justify-end gap-3 pt-4 border-t border-[#E5E9E4]">
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  className="px-4 py-2.5 rounded-sm border border-[#E5E9E4] text-sm"
                >
                  Ακύρωση
                </button>

                <button
                  type="submit"
                  disabled={saving}
                  data-testid="save-category-button"
                  className="px-5 py-2.5 rounded-sm bg-[#1E3A2F] text-white text-sm font-medium hover:bg-[#111C18] disabled:opacity-60"
                >
                  {saving
                    ? "Αποθήκευση..."
                    : "Αποθήκευση"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}